import { useEffect, useState } from 'react';
import type { AdminChecklist, AdminGradeField, AdminPathStep, PhotoRule } from '../lib/types';
import { api } from '../lib/api';
import { Banner, Area, Field, IconBtn, Lines, Num, Text, slugify } from './ui';
import { useAdminContent } from './useAdminContent';
import { EditModal } from './EditModal';
import { useConfirm } from '../components/ConfirmDialog';
import { Loading } from '../components/Loading';

/**
 * The four tool datasets.
 *
 * Checklists are per-row CRUD; the path, photo rules and grade fields are
 * replace-the-whole-list endpoints, so those three edit a local copy and save it in one
 * go. That difference is the API's, not a UI choice — see routes/admin.ts.
 */

export function ChecklistsAdmin() {
  const { bundle, loading, error, note, run } = useAdminContent();
  const { confirm, dialog } = useConfirm();
  const [draft, setDraft] = useState<Partial<AdminChecklist> | null>(null);

  if (loading) return <Loading />;
  if (!bundle) return <Banner error={error ?? 'Could not load content.'} />;

  async function save() {
    if (!draft?.title?.trim()) return;

    const confirmed = await confirm({
      title: draft._id ? 'Save changes to this checklist?' : 'Create this checklist?',
      body: 'Vendors see checklist changes immediately. Reordering items shifts existing ticks, because a tick is keyed on position.',
      confirmLabel: draft._id ? 'Save changes' : 'Create checklist',
    });
    if (!confirmed) return;

    const body = {
      slug: draft.slug?.trim() || slugify(draft.title),
      title: draft.title.trim(),
      blurb: draft.blurb ?? '',
      // Blank rows are an artefact of the editor, not content.
      items: (draft.items ?? []).map((i) => i.trim()).filter(Boolean),
    };
    const ok = draft._id
      ? await run(() => api.updateChecklist(draft._id!, body), 'Checklist saved.')
      : await run(() => api.createChecklist(body), 'Checklist created.');
    if (ok) setDraft(null);
  }

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Tools</div>
      <h1 className="vu-h1">Checklists</h1>
      <p className="vu-lede" style={{ maxWidth: '58ch' }}>
        Vendors tick these in the browser and print them. Ticks are stored per vendor, so
        renaming an item does not clear anyone's progress — but reordering shifts it,
        because a tick is keyed on position.
      </p>
      <Banner error={error} note={note} />

      {bundle.checklists.map((c) => (
        <div key={c._id} className="vu-card" style={{ padding: 'var(--space-5)', marginTop: 'var(--space-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
            <div>
              <h2 className="vu-h3" style={{ fontSize: 'var(--fs-h4)' }}>{c.title}</h2>
              <p className="vu-subtle" style={{ margin: '4px 0 0' }}>{c.blurb}</p>
            </div>
            <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
              <button type="button" className="vu-btn vu-btn-ghost" onClick={() => setDraft(c)}>Edit</button>
              <IconBtn
                label="Delete checklist"
                danger
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: `Delete “${c.title}”?`,
                      body: `This removes the checklist and its ${c.items.length} item(s), and every vendor's ticks against it. It cannot be undone.`,
                      confirmLabel: 'Delete checklist',
                      destructive: true,
                    });
                    if (ok) await run(() => api.deleteChecklist(c._id));
                  })();
                }}
              >
                ×
              </IconBtn>
            </div>
          </div>
          <ul className="vu-subtle" style={{ margin: 'var(--space-3) 0 0', paddingLeft: '1.2em', fontSize: 'var(--fs-sm)' }}>
            {c.items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      ))}

      <button type="button" className="vu-btn" style={{ marginTop: 'var(--space-5)' }} onClick={() => setDraft({ items: [''] })}>
        + New checklist
      </button>

      <EditModal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?._id ? 'Edit checklist' : 'New checklist'}
        description="Reordering items shifts vendors' existing ticks, because a tick is keyed on position."
        footer={
          <>
            <button type="button" className="vu-btn vu-btn-ghost" onClick={() => setDraft(null)}>Cancel</button>
            <button type="button" className="vu-btn" onClick={save}>Save</button>
          </>
        }
      >
        {draft && (
          <>
            <Field label="Title">
              <Text value={draft.title ?? ''} onChange={(title) => setDraft({ ...draft, title })} />
            </Field>
            <Field label="Slug" hint="Leave blank to derive it from the title.">
              <Text value={draft.slug ?? ''} onChange={(slug) => setDraft({ ...draft, slug })} />
            </Field>
            <Field label="Blurb">
              <Text value={draft.blurb ?? ''} onChange={(blurb) => setDraft({ ...draft, blurb })} />
            </Field>
            <Field label="Items">
              <Lines items={draft.items ?? []} onChange={(items) => setDraft({ ...draft, items })} placeholder="Checklist item" />
            </Field>
          </>
        )}
      </EditModal>

      {dialog}
    </div>
  );
}

export function PhotoRulesAdmin() {
  const { bundle, loading, error, note, run } = useAdminContent();
  const { confirm, dialog } = useConfirm();
  const [rules, setRules] = useState<PhotoRule[]>([]);

  // Seed the local copy once the bundle lands; this list saves as a whole.
  useEffect(() => {
    if (bundle) setRules(bundle.photoRules);
  }, [bundle]);

  if (loading) return <Loading />;
  if (!bundle) return <Banner error={error ?? 'Could not load content.'} />;

  const set = (i: number, patch: Partial<PhotoRule>) =>
    setRules(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Tools</div>
      <h1 className="vu-h1">Photo standard</h1>
      <p className="vu-lede" style={{ maxWidth: '60ch' }}>
        Each rule shows the version that passes review beside the one that gets rejected.
        Saving replaces the whole list.
      </p>
      <Banner error={error} note={note} />

      {rules.map((r, i) => (
        <div key={i} className="vu-card" style={{ padding: 'var(--space-4)', marginTop: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-3)' }}>
            <span className="vu-kicker" style={{ margin: 0 }}>Rule {i + 1}</span>
            <span style={{ flex: 1 }} />
            <IconBtn label="Remove rule" danger onClick={() => setRules(rules.filter((_, j) => j !== i))}>×</IconBtn>
          </div>
          <Field label="Rule">
            <Text value={r.rule} onChange={(v) => set(i, { rule: v })} />
          </Field>
          <div className="vu-grid vu-grid-2" style={{ gap: 'var(--space-4)' }}>
            <Field label="Passes">
              <Area value={r.good} onChange={(v) => set(i, { good: v })} rows={2} />
            </Field>
            <Field label="Rejected">
              <Area value={r.bad} onChange={(v) => set(i, { bad: v })} rows={2} />
            </Field>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 'var(--space-5)' }}>
        <button type="button" className="vu-btn vu-btn-ghost" onClick={() => setRules([...rules, { rule: '', good: '', bad: '' }])}>
          + Add rule
        </button>
        <button
          type="button"
          className="vu-btn"
          onClick={() => {
            void (async () => {
              const ok = await confirm({
                title: `Save ${rules.length} photo rule(s)?`,
                body: 'This replaces the whole list. Anything removed here is gone, and vendors see the new standard immediately.',
                confirmLabel: 'Save photo standard',
              });
              if (ok) await run(() => api.savePhotoRules(rules), 'Photo standard saved.');
            })();
          }}
        >
          Save all
        </button>
      </div>

      {dialog}
    </div>
  );
}

export function GradeFieldsAdmin() {
  const { bundle, loading, error, note, run } = useAdminContent();
  const { confirm, dialog } = useConfirm();
  const [fields, setFields] = useState<AdminGradeField[]>([]);

  useEffect(() => {
    if (bundle) setFields(bundle.gradeFields);
  }, [bundle]);

  if (loading) return <Loading />;
  if (!bundle) return <Banner error={error ?? 'Could not load content.'} />;

  const set = (i: number, patch: Partial<AdminGradeField>) =>
    setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));

  // The grader reports a score out of 100, so the server refuses anything else. Show the
  // running total rather than letting the author discover it in an error.
  const total = fields.reduce((sum, f) => sum + (Number(f.weight) || 0), 0);
  const balanced = Math.abs(total - 100) < 0.001;

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Tools</div>
      <h1 className="vu-h1">Listing checker</h1>
      <p className="vu-lede" style={{ maxWidth: '60ch' }}>
        The ten fields a vendor ticks, and what each is worth. Weights must total exactly
        100 — the score is out of 100, so anything else makes the tool lie.
      </p>
      <Banner error={error} note={note} />

      <div
        style={{
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-4)',
          fontWeight: 600,
          background: balanced ? 'var(--surface-sunken)' : 'var(--danger-bg)',
          color: balanced ? 'var(--green-500)' : 'var(--danger)',
        }}
      >
        Total weight: {total}{balanced ? ' ✓' : ' — must be 100'}
      </div>

      {fields.map((f, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 6 }}>
          <input className="vu-input" value={f.label} placeholder="Label" onChange={(e) => set(i, { label: e.target.value })} />
          <input
            className="vu-input"
            style={{ maxWidth: 200 }}
            value={f.slug}
            placeholder="slug"
            onChange={(e) => set(i, { slug: e.target.value })}
          />
          <div style={{ maxWidth: 110 }}>
            <Num value={f.weight} onChange={(weight) => set(i, { weight })} />
          </div>
          <IconBtn label="Remove field" danger onClick={() => setFields(fields.filter((_, j) => j !== i))}>×</IconBtn>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 'var(--space-5)' }}>
        <button
          type="button"
          className="vu-btn vu-btn-ghost"
          onClick={() => setFields([...fields, { slug: '', label: '', weight: 0 }])}
        >
          + Add field
        </button>
        <button
          type="button"
          className="vu-btn"
          disabled={!balanced}
          title={balanced ? undefined : 'Weights must total 100'}
          onClick={() => {
            void (async () => {
              const ok = await confirm({
                title: `Save ${fields.length} grader field(s)?`,
                body: 'This replaces the whole list. Vendors already part-way through the checker will see the new fields, and their existing ticks are kept only where a field slug is unchanged.',
                confirmLabel: 'Save listing checker',
              });
              if (ok) await run(() => api.saveGradeFields(fields), 'Listing checker saved.');
            })();
          }}
        >
          Save all
        </button>
      </div>

      {dialog}
    </div>
  );
}

export function PathAdmin() {
  const { bundle, loading, error, note, run } = useAdminContent();
  const { confirm, dialog } = useConfirm();
  const [steps, setSteps] = useState<AdminPathStep[]>([]);

  useEffect(() => {
    if (bundle) setSteps(bundle.path);
  }, [bundle]);

  if (loading) return <Loading />;
  if (!bundle) return <Banner error={error ?? 'Could not load content.'} />;

  const set = (i: number, patch: Partial<AdminPathStep>) =>
    setSteps(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  // `n` is the displayed step number and the sort key, so renumber on save rather than
  // asking the author to keep it consistent by hand.
  const renumbered = steps.map((s, i) => ({ ...s, n: i + 1 }));

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Content</div>
      <h1 className="vu-h1">The guided path</h1>
      <p className="vu-lede" style={{ maxWidth: '60ch' }}>
        The ordered walkthrough a new vendor follows. Each step points at a lesson by
        track and lesson slug — if either slug changes, fix it here too, because nothing
        enforces the link.
      </p>
      <Banner error={error} note={note} />

      {steps.map((s, i) => (
        <div key={i} className="vu-card" style={{ padding: 'var(--space-4)', marginTop: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--space-3)' }}>
            <span className="vu-kicker" style={{ margin: 0 }}>Step {i + 1}</span>
            <span style={{ flex: 1 }} />
            <IconBtn label="Remove step" danger onClick={() => setSteps(steps.filter((_, j) => j !== i))}>×</IconBtn>
          </div>
          <Field label="Title">
            <Text value={s.title} onChange={(v) => set(i, { title: v })} />
          </Field>
          <Field label="Body">
            <Area value={s.body} onChange={(v) => set(i, { body: v })} rows={2} />
          </Field>
          <div className="vu-grid vu-grid-2" style={{ gap: 'var(--space-4)' }}>
            <Field label="Track slug">
              <Text value={s.trackSlug} onChange={(v) => set(i, { trackSlug: v })} placeholder="listings" />
            </Field>
            <Field label="Lesson slug">
              <Text value={s.lessonSlug} onChange={(v) => set(i, { lessonSlug: v })} placeholder="variants" />
            </Field>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginTop: 'var(--space-5)' }}>
        <button
          type="button"
          className="vu-btn vu-btn-ghost"
          onClick={() => setSteps([...steps, { n: steps.length + 1, title: '', body: '', trackSlug: '', lessonSlug: '' }])}
        >
          + Add step
        </button>
        <button
          type="button"
          className="vu-btn"
          onClick={() => {
            void (async () => {
              const ok = await confirm({
                title: `Save the ${steps.length}-step path?`,
                body: 'This replaces every step. Steps are renumbered in the order shown, and nothing checks that the track and lesson slugs still exist.',
                confirmLabel: 'Save path',
              });
              if (ok) await run(() => api.savePath(renumbered), 'Path saved.');
            })();
          }}
        >
          Save all
        </button>
      </div>

      {dialog}
    </div>
  );
}
